import { Chat as ChatIcon } from "@mui/icons-material";
import type { CellContext, ColumnDef } from "@tanstack/react-table";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import AIChat, { type AIChatHandle } from "~/components/aiChat";
import ChatHistoryPanel from "~/components/aiChat/components/ChatHistoryPanel";
import DiffViewer, { type DiffViewerHandle } from "~/components/aiChat/diff";
import CommonDocumentHeader from "~/components/Document/commonDocumentHeader";
import documentStyles from "~/components/Document/documentStyles.module.scss";
import ExportModal, {
  formatSettings,
  type TableExportData,
} from "~/components/Document/exportModal";
import { MarkdownContent, markdownContentClassName } from "~/components/Document/MarkdownContent";
import MarkdownEditorPreview from "~/components/Document/MarkdownEditorPreview";
import KnowledgeIndicator from "~/components/Project/knowledge/KnowledgeIndicator";
import KnowledgeSidebar from "~/components/Project/knowledge/KnowledgeSidebar";
import { StructuredDiffErrorBoundary } from "~/components/StructuredDiffViewer/StructuredDiffErrorBoundary";
import {
  type StructuredDiffPayload,
  StructuredDiffViewer,
} from "~/components/StructuredDiffViewer/StructuredDiffViewer";
import Button from "~/components/ui/button";
import ItemSettingModal from "~/components/ui/itemModal";
import type { FieldData, FieldOptions } from "~/components/ui/itemModal/types";
import Modal from "~/components/ui/modal";
import ValidationWarningModal from "~/components/ui/validationWarningModal";
import { TOAST_MESSAGES } from "~/constants/messages";
import FieldDesignTabs from "~/features/fieldDesign/fieldDesignTabs";
import { AIChatApi } from "~/functions/api/aiChat";
import { useNavigationPrompt } from "~/functions/navigationHooks";
import { useKnowledgeCount } from "~/hooks/useKnowledgeCount";
import { useMarkdownRenderer } from "~/hooks/useMarkdownRenderer";
import { useProjectMembers } from "~/hooks/useProjectMembers";
import { useProjectPermission } from "~/hooks/useProjectPermission";
import { useStructuredDocumentApply } from "~/hooks/useStructuredDocumentApply";
import DeleteIcon from "~/images/delete_row.svg?react";
import {
  acquireLock,
  addField,
  clearError,
  deleteField,
  fetchFieldDesign,
  releaseLock,
  reorderFields,
  resetData,
  setAppData,
  setData,
  setDocumentInfo,
  updateField,
  updateFieldCodeWithCascade,
  updateFieldDesign,
  updateMarkdownContent,
  updateModifiedAt,
} from "~/slices/fieldDesignSlice";
import { initializeSessions } from "~/slices/projectSlice";
import type { AppDispatch, RootState } from "~/store";
import {
  cleanupSafeReferences,
  countFieldsForApp,
  deleteCriticalFields,
  MAX_FIELDS_PER_APP,
  type ValidationResult,
  validateBeforeSave,
  validateFieldCode,
} from "~/utils/fieldDesignValidation";
// TODO: テスト完了後に削除
import { FIELD_DESIGN_APP_DATA, FIELD_DESIGN_FIELD_DATA } from "../../../tests/fixtures";
import chatStyles from "../systemProposal/styles.module.scss";
import styles from "./index.module.scss";

/** kintoneが自動管理するシステムフィールド。フィールドタイプ選択肢から除外する */
const SYSTEM_FIELD_TYPES = new Set([
  "RECORD_NUMBER",
  "CREATOR",
  "MODIFIER",
  "CREATED_TIME",
  "UPDATED_TIME",
  "STATUS",
  "STATUS_ASSIGNEE",
  "CATEGORY",
  "ASSIGNEE",
  "CHECKBOX", // 旧フィルタ互換（CHECK_BOXが正式）
]);

function createFieldData(
  app_id: string,
  フィールド名: string,
  フィールドコード: string,
  フィールドタイプ: string,
  options?: FieldOptions,
  id?: string,
): FieldData {
  const fieldData: FieldData = {
    id: id || uuidv4(),
    app_id,
    フィールド名,
    フィールドコード,
    フィールドタイプ,
    options: options || {},
  };

  return fieldData;
}

/** フィールドコードセル — 安定した関数参照により flexRender でのリマウントを防止 */
const FieldCodeCell = ({ getValue, row, table }: CellContext<FieldData, unknown>) => {
  const value = getValue() as string;
  const fieldId = row.original["id"];
  const meta = table.options.meta as
    | {
        fieldCodeErrors?: Map<string, string>;
        isEditable?: boolean;
        updateData?: (rowIndex: number, columnId: string, value: string) => void;
      }
    | undefined;
  const error = meta?.fieldCodeErrors?.get(fieldId);
  const isEditable = meta?.isEditable ?? false;

  if (!isEditable) {
    return <span style={{ color: "#666" }}>{value}</span>;
  }
  return (
    <div>
      <textarea
        rows={1}
        value={value}
        onChange={(e) => {
          meta?.updateData?.(row.index, "フィールドコード", e.target.value);
        }}
        style={error ? { border: "2px solid #d32f2f", backgroundColor: "#fff5f5" } : undefined}
      />
      {error && <div style={{ color: "#d32f2f", fontSize: "11px", marginTop: "2px" }}>{error}</div>}
    </div>
  );
};

const FieldDesign = () => {
  const { renderMarkdown } = useMarkdownRenderer();
  const dispatch = useDispatch<AppDispatch>();
  const { selectedId } = useParams<{ selectedId: string }>();
  // プロジェクトメンバーの読み込み（戻り値未使用でも呼び出し）
  useProjectMembers();
  const navigate = useNavigate();
  const { currentProject, currentUser, organization } = useSelector(
    (state: RootState) => state.auth,
  );
  const { canAccessChat } = useProjectPermission();

  // チャットアイコンの表示可否を判定
  const shouldShowChatIcon = canAccessChat();

  const {
    data: fieldTableData,
    copyData,
    appData,
    isEditable,
    isLoading,
    error,
    documentInfo,
    isMarkdown,
    markdownContent,
    rawContent,
  } = useSelector((state: RootState) => state.fieldDesign);

  const [currentTabIndex, setCurrentTabIndex] = React.useState<number>(0);
  const [showItemModal, setShowItemModal] = React.useState<boolean>(false);
  const [currentFieldData, setCurrentFieldData] = React.useState<FieldData | null>(null);
  const [showExportModal, setShowExportModal] = React.useState<boolean>(false);
  const [showValidationModal, setShowValidationModal] = React.useState<boolean>(false);
  const [validationResult, setValidationResult] = React.useState<ValidationResult | null>(null);
  const shouldUseMarkdownMode = isMarkdown;
  const isStructuredDocument = !isMarkdown;

  // ナレッジインジケーター用のカウント取得
  const { count: knowledgeCount, totalCount: knowledgeTotalCount } =
    useKnowledgeCount("field-design");
  // ナレッジサイドバーの状態
  const [isKnowledgeSidebarOpen, setIsKnowledgeSidebarOpen] = React.useState(false);
  const { currentSession } = useSelector((state: RootState) => state.project);
  const [showAIChat, setShowAIChat] = React.useState(true);
  const [isDiffModalOpen, setDiffModalOpen] = React.useState(false);
  const [showChatHistory, setShowChatHistory] = React.useState(false);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [threads, setThreads] = React.useState<
    { id?: string; threadId?: string; title?: string; createdAt?: string; updatedAt?: string }[]
  >([]);
  const aiChatRef = React.useRef<AIChatHandle>(null);
  const diffViewerRef = React.useRef<DiffViewerHandle>(null);
  const [currentThreadId, setCurrentThreadId] = React.useState<string>("");
  const [documentBefore, setDocumentBefore] = React.useState<unknown>(null);
  const [documentAfter, setDocumentAfter] = React.useState<unknown>(null);
  const [structuredDiff, setStructuredDiff] = React.useState<StructuredDiffPayload | null>(null);
  const [isDiffSaving, setIsDiffSaving] = React.useState(false);
  const [isDiffValid, setIsDiffValid] = React.useState(true);
  const [diffViewerHeader, setDiffViewerHeader] = React.useState<React.ReactNode>(null);
  const handleStructuredDocumentApply = useStructuredDocumentApply({
    acquireLock: () => dispatch(acquireLock()).unwrap(),
    releaseLock: () => dispatch(releaseLock()),
    fetchDocument: () => dispatch(fetchFieldDesign()),
    organizationId: currentProject?.id ? currentUser.organization_id : "",
    projectId: currentProject?.id || "",
    documentId: currentProject?.outputs?.["field-design"]?.id || "",
    setIsDiffSaving,
    logPrefix: "FieldDesign",
  });

  // フィールドタイプの日本語ラベル定義（kintone公式ヘルプ準拠）
  const fieldTypeLabels: { [key: string]: string } = {
    RECORD_NUMBER: "レコード番号",
    SINGLE_LINE_TEXT: "文字列（1行）",
    MULTI_LINE_TEXT: "文字列（複数行）",
    NUMBER: "数値",
    CALC: "計算",
    DATE: "日付",
    TIME: "時刻",
    DATETIME: "日時",
    RADIO_BUTTON: "ラジオボタン",
    DROP_DOWN: "ドロップダウン",
    FILE: "添付ファイル",
    USER_SELECT: "ユーザー選択",
    GROUP_SELECT: "グループ選択",
    ORGANIZATION_SELECT: "組織選択",
    RICH_TEXT: "リッチエディター",
    SUBTABLE: "テーブル",
    REFERENCE_TABLE: "関連レコード一覧",
    LOOKUP: "ルックアップ",
    LINK: "リンク",
    CHECK_BOX: "チェックボックス",
    MULTI_SELECT: "複数選択",
    GROUP: "グループ",
    CREATOR: "作成者",
    MODIFIER: "更新者",
    CREATED_TIME: "作成日時",
    UPDATED_TIME: "更新日時",
    STATUS: "ステータス",
    STATUS_ASSIGNEE: "作業者",
    CATEGORY: "カテゴリー",
    ASSIGNEE: "担当者",
  };

  // フィールドタイプに応じたデフォルトオプションを生成する関数
  const getDefaultOptionsForFieldType = (fieldType: string): FieldOptions => {
    const baseOptions = {
      noLabel: false,
      required: false,
    };

    switch (fieldType) {
      case "SINGLE_LINE_TEXT":
      case "MULTI_LINE_TEXT":
      case "RICH_TEXT":
        return {
          ...baseOptions,
          minLength: "",
          maxLength: "",
          defaultValue: "",
          unique: false,
        };
      case "NUMBER":
        return {
          ...baseOptions,
          minValue: "",
          maxValue: "",
          digit: false,
          displayScale: "",
          unit: "",
          unitPosition: "AFTER",
        };
      case "DATE":
        return {
          ...baseOptions,
          defaultNowValue: false,
        };
      case "LINK":
        return {
          ...baseOptions,
          protocol: "WEB",
        };
      case "CHECK_BOX":
        return {
          ...baseOptions,
          order: "VERTICAL",
          options: [
            { id: "1", value: "オプション1" },
            { id: "2", value: "オプション2" },
          ],
        };
      case "DROP_DOWN":
      case "MULTI_SELECT":
        return {
          ...baseOptions,
          options: [
            { id: "1", value: "オプション1" },
            { id: "2", value: "オプション2" },
          ],
        };
      case "RADIO_BUTTON":
        return {
          ...baseOptions,
          order: "VERTICAL",
          options: [
            { id: "1", value: "オプション1" },
            { id: "2", value: "オプション2" },
          ],
        };
      case "REFERENCE_TABLE":
        return {
          ...baseOptions,
          referenceTable: {
            relatedApp: {
              app: "",
              code: "",
            },
            condition: {
              field: "",
              relatedField: "",
            },
            displayFields: [],
            sort: "",
            size: "5",
          },
        };
      case "LOOKUP":
        return {
          ...baseOptions,
          lookup: {
            relatedApp: {
              app: "",
              code: "",
            },
            relatedKeyField: "",
            fieldMappings: [],
            lookupPickerFields: [],
            sort: "",
          },
        };
      default:
        return baseOptions;
    }
  };

  const openItemSettingModal = (fieldData: FieldData) => {
    setCurrentFieldData(fieldData);
    setShowItemModal(true);
  };

  const closeItemSettingModal = () => {
    setShowItemModal(false);
    setCurrentFieldData(null);
  };

  const saveItemSettings = (updatedOptions: FieldOptions) => {
    if (currentFieldData) {
      dispatch(
        updateField({
          fieldId: currentFieldData["id"],
          updates: {
            options: updatedOptions,
          },
        }),
      );
    }
    closeItemSettingModal();
  };

  const fieldDesignColumns: ColumnDef<FieldData>[] = [
    { accessorKey: "フィールド名", header: "フィールド名" },
    {
      accessorKey: "フィールドコード",
      header: "フィールドコード",
      cell: FieldCodeCell,
    },
    {
      accessorKey: "フィールドタイプ",
      header: "タイプ",
      meta: { inputType: "select" as const },
      cell: ({ getValue, row, table }) => {
        const value = getValue() as string;
        if (!isEditable) {
          return <span style={{ color: "#666" }}>{fieldTypeLabels[value] || value}</span>;
        }
        return (
          <select
            value={value}
            onChange={(e) => {
              const rowIndex = row.index;
              const meta = table.options.meta as
                | { updateData?: (rowIndex: number, columnId: string, value: string) => void }
                | undefined;
              meta?.updateData?.(rowIndex, "フィールドタイプ", e.target.value);
            }}
          >
            {Object.entries(fieldTypeLabels)
              .filter(([key]) => !SYSTEM_FIELD_TYPES.has(key))
              .map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
          </select>
        );
      },
    },
    {
      accessorKey: "settings",
      header: "詳細設定",
      cell: ({ row }) => (
        <button
          type="button"
          onClick={() => openItemSettingModal(row.original as FieldData)}
          style={{
            borderRadius: "4px",
            padding: "4px 8px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <span style={{ color: "#0066cc", fontWeight: "bold" }}>設定</span>
        </button>
      ),
    },
    {
      accessorKey: "delete",
      header: "",
      cell: ({ row }) =>
        isEditable ? (
          <button
            type="button"
            onClick={() => deleteRow(row.index, currentTabIndex)}
            style={{ background: "none", border: "none", cursor: "pointer" }}
          >
            <DeleteIcon width={16} height={16} />
          </button>
        ) : null,
    },
  ];

  // 現在タブのアプリIDを取得
  const currentAppId = currentTabIndex < appData.length ? appData[currentTabIndex]!.app_id : null;

  // 現在タブのフィールド数と上限を計算
  const currentAppFieldCount = currentAppId
    ? countFieldsForApp(fieldTableData.filter((f) => f.app_id === currentAppId))
    : 0;
  const isFieldLimitReached = currentAppFieldCount >= MAX_FIELDS_PER_APP;

  // 新しい行を追加する関数を修正
  const addNewRow = (selectedFieldType: string) => {
    if (shouldUseMarkdownMode) {
      setShowItemModal(false);
      return;
    }
    if (fieldTableData) {
      const appId = currentAppId ?? "1356";
      // ユニークなフィールド名・コードを生成
      const newIndex = fieldTableData.filter((f) => f.app_id === appId).length + 1;
      const fieldName = `新しいフィールド_${newIndex}`;
      const newRow = createFieldData(
        appId,
        fieldName,
        `field_${Date.now()}_${newIndex}`,
        selectedFieldType,
        getDefaultOptionsForFieldType(selectedFieldType),
      );
      dispatch(addField(newRow));
    }
    setShowItemModal(false);
  };

  const getExportData = (): TableExportData => {
    const name = "フィールド設計書";

    if (shouldUseMarkdownMode) {
      return {
        headers: [],
        rows: [],
        name,
        markdownContent: markdownContent,
      };
    }

    const headers: string[] = [];
    const rows: string[][] = [];

    // フィールドの詳細データを含めてエクスポート用のデータを作成
    headers.push(
      "app_id",
      "app_name",
      "フィールド名",
      "フィールドコード",
      "フィールドタイプ",
      "required",
      "noLabel",
    );

    // アプリIDからアプリ名を取得するマッピングを作成
    const appNameMap: Record<string, string> = {};
    appData.forEach((app) => {
      appNameMap[app.app_id] = app.app_name;
    });

    fieldTableData.forEach((row) => {
      // アプリ名を取得（見つからない場合はデフォルト値を使用）
      const appName = appNameMap[row.app_id] || `アプリ ${row.app_id}`;

      // 基本情報を配列に追加
      const rowData = [
        row.app_id, // アプリID
        appName, // アプリ名
        row.フィールド名,
        row.フィールドコード,
        row.フィールドタイプ,
        row.options?.required ? "true" : "false", // 必須項目
        row.options?.noLabel ? "true" : "false", // ラベル表示
      ];

      if (row.フィールドタイプ === "SUBTABLE" && row.options?.fields) {
        const subFields = Object.values(row.options.fields)
          .map((field: Record<string, unknown>) => {
            const base = `フィールド名: ${field["label"]}, コード: ${field["code"]}, タイプ: ${field["type"]}`;
            const required =
              field["required"] !== undefined
                ? `, 必須: ${field["required"] ? "はい" : "いいえ"}`
                : "";
            const noLabel =
              field["noLabel"] !== undefined
                ? `, ラベル: ${field["noLabel"] ? "非表示" : "表示"}`
                : "";

            let optionDetail = "";
            const fieldOptions = field["options"] as Record<string, unknown> | undefined;
            if (fieldOptions) {
              if (
                field["type"] === "DROP_DOWN" ||
                field["type"] === "RADIO_BUTTON" ||
                field["type"] === "CHECK_BOX" ||
                field["type"] === "MULTI_SELECT"
              ) {
                const innerOptions = fieldOptions["options"];
                if (innerOptions && Array.isArray(innerOptions)) {
                  const optionsString = innerOptions
                    .map((opt: Record<string, unknown>) => opt["value"])
                    .join(", ");
                  optionDetail = `選択肢: ${optionsString}`;
                }
              } else {
                optionDetail = formatSettings(fieldOptions);
              }
            }

            return `・${base}${required}${noLabel}${optionDetail ? `\n  ${optionDetail}` : ""}`;
          })
          .join("\n\n");
        rowData.push(subFields);
      } else if (
        row.フィールドタイプ === "SINGLE_LINE_TEXT" ||
        row.フィールドタイプ === "MULTI_LINE_TEXT" ||
        row.フィールドタイプ === "RICH_TEXT"
      ) {
        rowData.push(
          row.options?.minLength || "",
          row.options?.maxLength || "",
          row.options?.defaultValue || "",
          row.options?.unique ? "true" : "false",
        );
      } else if (row.フィールドタイプ === "NUMBER") {
        rowData.push(
          row.options?.minValue || "",
          row.options?.maxValue || "",
          row.options?.unit || "",
          row.options?.unitPosition || "",
          row.options?.digit ? "true" : "false",
          row.options?.displayScale || "",
        );
      } else if (
        row.フィールドタイプ === "DATE" ||
        row.フィールドタイプ === "DATETIME" ||
        row.フィールドタイプ === "TIME"
      ) {
        rowData.push(row.options?.defaultNowValue ? "true" : "false");
      } else if (
        ["DROP_DOWN", "RADIO_BUTTON", "CHECK_BOX", "MULTI_SELECT"].includes(row.フィールドタイプ)
      ) {
        let optionsString = "";
        if (row.options?.options && Array.isArray(row.options.options)) {
          optionsString = row.options.options.map((opt) => opt.value).join(", ");
        }
        rowData.push(optionsString);
      } else if (row.フィールドタイプ === "LOOKUP") {
        if (row.options?.lookup) {
          rowData.push(
            row.options.lookup.relatedApp ? JSON.stringify(row.options.lookup.relatedApp) : "",
            row.options.lookup.relatedKeyField || "",
            row.options.lookup.fieldMappings
              ? JSON.stringify(row.options.lookup.fieldMappings)
              : "",
            row.options.lookup.lookupPickerFields
              ? JSON.stringify(row.options.lookup.lookupPickerFields)
              : "",
            row.options.lookup.sort ? JSON.stringify(row.options.lookup.sort) : "",
          );
        } else {
          rowData.push("", "", "");
        }
      } else if (row.フィールドタイプ === "REFERENCE_TABLE") {
        if (row.options?.referenceTable) {
          rowData.push(
            row.options.referenceTable.relatedApp
              ? JSON.stringify(row.options.referenceTable.relatedApp)
              : "",
            row.options.referenceTable.condition
              ? JSON.stringify(row.options.referenceTable.condition)
              : "",
            row.options.referenceTable.displayFields
              ? JSON.stringify(row.options.referenceTable.displayFields)
              : "",
            row.options.referenceTable.sort ? JSON.stringify(row.options.referenceTable.sort) : "",
            row.options.referenceTable.size ? JSON.stringify(row.options.referenceTable.size) : "",
          );
        } else {
          rowData.push("", "", "");
        }
      } else if (row.フィールドタイプ === "USER_SELECT") {
        if (row.options) {
          rowData.push(
            row.options.noLabel ? JSON.stringify(row.options.noLabel) : "",
            row.options["openGroup"] ? JSON.stringify(row.options["openGroup"]) : "",
          );
        } else {
          rowData.push("", "");
        }
      } else if (row.フィールドタイプ === "LINK") {
        rowData.push(row.options?.protocol || "");
      } else if (row.フィールドタイプ === "FILE") {
        rowData.push(row.options?.["thumbnailSize"] || "");
      } else if (row.フィールドタイプ === "GROUP") {
        rowData.push(
          row.options?.noLabel ? JSON.stringify(row.options.noLabel) : "",
          row.options?.required ? JSON.stringify(row.options.required) : "",
        );
      }
      rows.push(rowData);
    });

    return { headers, rows, name };
  };

  // 現在選択されているアプリのデータだけをフィルターする関数
  const getFilteredDataForCurrentTab = (tabIndex: number): FieldData[] => {
    if (tabIndex >= 0 && tabIndex < appData.length && appData) {
      const currentAppId = appData[tabIndex]!.app_id;
      return fieldTableData.filter((field) => field.app_id === currentAppId);
    }
    return [];
  };

  const deleteRow = (rowIndex: number, tabIndex: number) => {
    if (shouldUseMarkdownMode) return;
    const filteredData = getFilteredDataForCurrentTab(tabIndex);
    const selectedRow = filteredData[rowIndex];

    if (selectedRow) {
      dispatch(deleteField(selectedRow["id"]));
    }
  };

  // 選択肢型フィールドタイプ
  const choiceFieldTypes = new Set(["DROP_DOWN", "RADIO_BUTTON", "CHECK_BOX", "MULTI_SELECT"]);

  // フィールドコードバリデーションエラー state
  const [fieldCodeErrors, setFieldCodeErrors] = React.useState<Map<string, string>>(new Map());

  const updateData = (
    rowIndex: number,
    columnId: string,
    value: string | number,
    tabIndex: number,
  ) => {
    if (shouldUseMarkdownMode) return;
    const filteredData = getFilteredDataForCurrentTab(tabIndex);
    const targetField = filteredData[rowIndex];
    if (!targetField) return;

    // フィールドコード変更: バリデーション + 連鎖更新
    if (columnId === "フィールドコード") {
      const newCode = String(value);
      const error = validateFieldCode(
        newCode,
        targetField["id"],
        targetField.app_id,
        fieldTableData,
      );
      setFieldCodeErrors((prev) => {
        const next = new Map(prev);
        if (error) {
          next.set(targetField["id"], error);
        } else {
          next.delete(targetField["id"]);
        }
        return next;
      });
      dispatch(
        updateFieldCodeWithCascade({
          fieldId: targetField["id"],
          oldFieldCode: targetField.フィールドコード,
          newFieldCode: newCode,
          appId: targetField.app_id,
        }),
      );
      return;
    }

    // フィールドタイプ変更: オプション構造変換
    if (columnId === "フィールドタイプ") {
      const oldType = targetField.フィールドタイプ;
      const newType = String(value);
      if (oldType === newType) return;

      const oldIsChoice = choiceFieldTypes.has(oldType);
      const newIsChoice = choiceFieldTypes.has(newType);

      let newOptions: FieldOptions;
      if (oldIsChoice && newIsChoice) {
        // 選択肢型同士: オプション（選択肢リスト）を保持
        newOptions = { ...targetField.options };
      } else {
        newOptions = getDefaultOptionsForFieldType(newType);
      }

      dispatch(
        updateField({
          fieldId: targetField["id"],
          updates: {
            フィールドタイプ: newType,
            options: newOptions,
          },
        }),
      );
      return;
    }

    // その他のフィールド: 従来通り
    dispatch(
      updateField({
        fieldId: targetField["id"],
        updates: {
          [columnId]: value,
        },
      }),
    );
  };

  const handleLock = async () => {
    if (!currentProject.outputs?.["field-design"]?.id) {
      toast.error(TOAST_MESSAGES.DOCUMENT.FIELD_DESIGN_NOT_FOUND);
      return;
    }

    dispatch(acquireLock());
  };

  const handleCancel = async () => {
    dispatch(releaseLock());
    dispatch(resetData());
    setFieldCodeErrors(new Map());
  };

  const handleEdit = async () => {
    handleLock();
  };

  const handleExportClick = () => {
    setShowExportModal(true);
  };

  const handleSave = async () => {
    // Markdownモードの場合は直接保存
    if (shouldUseMarkdownMode) {
      dispatch(updateModifiedAt());
      await dispatch(updateFieldDesign());
      return;
    }

    // 構造化データの場合はバリデーションを実行
    // validateBeforeSave 内で全フィールドコードの kintone 形式チェックも実施
    const result = validateBeforeSave(fieldTableData, copyData, appData);

    // 削除されたフィールドや無効な参照がある場合は確認ダイアログを表示
    const hasIssues =
      result.deletedFields.length > 0 ||
      result.criticalReferences.length > 0 ||
      result.safeCleanableReferences.length > 0 ||
      result.blockingErrors.length > 0;
    if (hasIssues) {
      setValidationResult(result);
      setShowValidationModal(true);
      return;
    }

    // 問題がなければ直接保存（normalizedData を Redux に反映してから保存）
    dispatch(setData(result.normalizedData));
    dispatch(updateModifiedAt());
    await dispatch(updateFieldDesign());
  };

  // 致命的な参照がない場合の保存処理（補助参照のみクリーンアップ）
  const handleConfirmSave = async () => {
    setShowValidationModal(false);

    if (validationResult) {
      // normalizedData（自動補完済み）をベースにクリーンアップを実施
      const baseData = validationResult.normalizedData;
      if (validationResult.safeCleanableReferences.length > 0) {
        // 安全にクリーンアップできる参照（補助フィールドのみ）をクリーンアップ
        const result = cleanupSafeReferences(baseData, validationResult.safeCleanableReferences);
        dispatch(setData(result.data));
      } else {
        dispatch(setData(baseData));
      }
    }

    // 更新を適用後に保存
    dispatch(updateModifiedAt());
    await dispatch(updateFieldDesign());
    setValidationResult(null);
  };

  // 致命的な参照があるフィールドを削除して保存
  const handleConfirmSaveWithDeletion = async () => {
    setShowValidationModal(false);

    if (validationResult) {
      // normalizedData（自動補完済み）をベースに削除・クリーンアップを実施
      let updatedData = [...validationResult.normalizedData];

      // 致命的な参照を持つフィールドを削除
      if (validationResult.criticalReferences.length > 0) {
        const deleteResult = deleteCriticalFields(updatedData, validationResult.criticalReferences);
        updatedData = deleteResult.data;
      }

      // 安全にクリーンアップできる参照をクリーンアップ
      if (validationResult.safeCleanableReferences.length > 0) {
        const cleanupResult = cleanupSafeReferences(
          updatedData,
          validationResult.safeCleanableReferences,
        );
        updatedData = cleanupResult.data;
      }

      dispatch(setData(updatedData));
    }

    // 更新を適用後に保存
    dispatch(updateModifiedAt());
    await dispatch(updateFieldDesign());
    setValidationResult(null);
  };

  const handleCancelValidation = () => {
    setShowValidationModal(false);
    setValidationResult(null);
  };

  const toggleEditMode = () => {
    if (isEditable) {
      handleSave(); // 保存処理を実行
    } else {
      handleEdit();
    }
  };

  // TODO: テスト完了後に false に戻すか、このブロックを削除
  const USE_TEST_DATA = false;

  // モックデータでテーブルを初期化（本番環境では削除または置き換え）
  useEffect(() => {
    // テストモード: テストデータを直接ロード
    if (USE_TEST_DATA) {
      dispatch(setAppData(FIELD_DESIGN_APP_DATA));
      dispatch(setData(FIELD_DESIGN_FIELD_DATA));
      // テスト用のダミーdocumentInfoを設定
      dispatch(
        setDocumentInfo({
          createdAt: new Date().toISOString(),
          modifiedAt: new Date().toISOString(),
          modifiedBy: { display_name: "テストユーザー" },
        }),
      );
      return;
    }

    if (!currentProject) {
      // プロジェクトが選択されていない場合はホームページにリダイレクト
      navigate(`/${currentUser.organization_id}/home`);
      return;
    }

    // URLパラメータから取得、なければReduxストアから取得
    const documentId = selectedId || currentProject.outputs?.["field-design"]?.id;

    if (!documentId) {
      toast.error(TOAST_MESSAGES.DOCUMENT.FIELD_DESIGN_NOT_GENERATED);
      navigate(`/${currentUser.organization_id}/project/${currentProject.id}/dashboard`);
      return;
    }

    if (!organization) return;

    dispatch(fetchFieldDesign(documentId));
  }, [dispatch, currentProject, currentUser, navigate, selectedId, organization]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isEditable) {
        event.preventDefault();
        event.returnValue = ""; // Chrome用: 空文字を指定
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isEditable]);

  // エラーメッセージの表示
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // 編集中の場合、ナビゲーションをブロック
  useNavigationPrompt(
    isEditable,
    "編集内容が保存されていません。ページを離れてもよろしいですか？",
    async () => {
      await dispatch(updateFieldDesign());
    },
    handleCancel,
  );

  // Initialize session IDs (reinit on document change)
  const fieldDesignDocId = currentProject?.outputs?.["field-design"]?.id;
  useEffect(() => {
    if (currentProject?.id && currentUser?.id) {
      dispatch(initializeSessions(fieldDesignDocId || ""));
    }
  }, [currentProject?.id, currentUser?.id, fieldDesignDocId, dispatch]);

  // currentSession.threadId が変更されたら currentThreadId を更新
  useEffect(() => {
    if (currentSession?.threadId) {
      setCurrentThreadId(currentSession.threadId);
    }
  }, [currentSession?.threadId]);

  // 履歴を更新する共通関数
  const refreshThreadHistory = async () => {
    // スレッド作成時や手動リロード時に履歴を更新
    setHistoryLoading(true);
    try {
      const api = new AIChatApi();
      const agentId = "commonAgent";
      const rid = currentSession?.resourceId;
      if (!rid) return;
      const list = await api.getThreads(agentId, rid, 10);
      setThreads(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("[FieldDesign] Failed to refresh thread history:", e);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleApprovedDocumentChange = async (approvedDocument: string) => {
    setIsDiffSaving(true);
    try {
      await dispatch(acquireLock());
      dispatch(updateMarkdownContent(approvedDocument));
      await dispatch(updateFieldDesign());
      await dispatch(fetchFieldDesign());
    } catch (error) {
      console.error("Update failed:", error);
      await dispatch(releaseLock()).catch(() => {});
    } finally {
      setIsDiffSaving(false);
    }
  };

  const handleDiffClose = () => {
    setIsDiffSaving(false);
    setDiffModalOpen(false);
    setShowAIChat(false);
    setDocumentBefore(null);
    setDocumentAfter(null);
    setStructuredDiff(null);
    setDiffViewerHeader(null);
    setIsDiffValid(true);
  };

  const handleDiffApply = async () => {
    if (isDiffSaving) return;
    if (isStructuredDocument && documentAfter) {
      // For structured documents, save content directly via DocumentApi
      // bypassing the thunk which would serialize from Redux state arrays
      const content =
        typeof documentAfter === "string" ? documentAfter : JSON.stringify(documentAfter);
      const success = await handleStructuredDocumentApply(content);
      if (success) {
        handleDiffClose();
      } else {
        toast.error("ドキュメントの保存に失敗しました");
      }
    } else {
      void diffViewerRef.current?.applyChanges();
    }
  };

  if (isLoading) {
    return <div>ロード中...</div>;
  }

  const handleHistoryClick = () => {
    const pathSegments = window.location.pathname.split("/");
    pathSegments.pop();
    navigate(`${pathSegments.join("/")}/history`);
  };

  return (
    <div className={styles["fieldDesign"]}>
      {documentInfo && (
        <CommonDocumentHeader
          title="フィールド設計書"
          documentType="field-design"
          createdAt={documentInfo.createdAt}
          modifiedAt={documentInfo.modifiedAt}
          {...(documentInfo.modifiedBy && { modifiedBy: documentInfo.modifiedBy })}
          isEditable={isEditable}
          isLoading={isLoading}
          handleCancel={handleCancel}
          toggleEditMode={toggleEditMode}
          handleExportClick={handleExportClick}
          handleHistoryClick={handleHistoryClick}
          showEditButton={true}
          showExportButton={true}
        />
      )}
      {showValidationModal && validationResult && (
        <ValidationWarningModal
          isOpen={showValidationModal}
          onClose={handleCancelValidation}
          onConfirm={handleConfirmSave}
          onConfirmWithDeletion={handleConfirmSaveWithDeletion}
          validationResult={validationResult}
        />
      )}

      {showExportModal && (
        <ExportModal
          documentType="field-design"
          onClose={() => setShowExportModal(false)}
          exportData={{
            type: "table",
            exportData: getExportData(),
            name: "field-design",
          }}
        />
      )}
      {!shouldUseMarkdownMode && showItemModal && currentFieldData && (
        <ItemSettingModal
          isOpen={showItemModal}
          onClose={closeItemSettingModal}
          fieldData={currentFieldData}
          onSave={saveItemSettings}
          isEditable={isEditable}
          fieldTableData={fieldTableData}
          appDataMap={appData.reduce(
            (map, app) => {
              map[app.app_id] = app.app_name;
              return map;
            },
            {} as Record<string, string>,
          )}
        />
      )}

      <div
        style={{
          display: "flex",
          gap: "24px",
          alignItems: "stretch",
          position: "relative",
          minHeight: "calc(100vh - 196px)",
        }}
      >
        <div style={{ flex: "1 1 auto" }}>
          {shouldUseMarkdownMode ? (
            <div className={styles["document-content"]}>
              <div className={documentStyles["markdown-mode-content"]}>
                {isEditable ? (
                  <MarkdownEditorPreview
                    value={markdownContent}
                    onChange={(value) => dispatch(updateMarkdownContent(value))}
                    previewContent={renderMarkdown(markdownContent || "")}
                    textareaClassName={documentStyles["markdown-editor"] || ""}
                    previewClassName={markdownContentClassName}
                  />
                ) : (
                  <MarkdownContent>{renderMarkdown(markdownContent || "")}</MarkdownContent>
                )}
              </div>
            </div>
          ) : (
            <>
              <FieldDesignTabs
                tabTitles={appData.map((app) => app.app_name)}
                dataSets={appData.map(
                  (_, index) => getFilteredDataForCurrentTab(index) as FieldData[],
                )}
                columns={appData.map(() => fieldDesignColumns as ColumnDef<FieldData>[])}
                updateData={updateData}
                isEditable={isEditable}
                onTabChange={setCurrentTabIndex}
                tableMeta={{ fieldCodeErrors }}
                onReorder={(newData: FieldData[], tabIndex: number) => {
                  const currentAppId = appData[tabIndex]?.app_id;
                  dispatch(
                    reorderFields({
                      appId: currentAppId!,
                      newOrder: newData,
                    }),
                  );
                }}
              />

              {isEditable && (
                <div className={styles["field-add-area"]}>
                  <span
                    className={
                      isFieldLimitReached ? styles["field-count--limit"] : styles["field-count"]
                    }
                  >
                    {currentAppFieldCount} / {MAX_FIELDS_PER_APP} フィールド
                  </span>
                  {isFieldLimitReached && (
                    <span className={styles["field-limit-message"]}>
                      フィールド数の上限（{MAX_FIELDS_PER_APP}
                      件）に達しました。これ以上追加できません。
                    </span>
                  )}
                  <Button
                    label="追加"
                    onClick={() => addNewRow("SINGLE_LINE_TEXT")}
                    styleType="add-row"
                    icon="addRowIcon"
                    disabled={isFieldLimitReached}
                  />
                </div>
              )}
            </>
          )}
        </div>
        {!showAIChat && (
          <KnowledgeSidebar
            isOpen={isKnowledgeSidebarOpen}
            onClose={() => setIsKnowledgeSidebarOpen(false)}
            documentType="field-design"
          />
        )}
        {showAIChat && (
          <div style={{ display: "flex", alignItems: "stretch", flex: "0 0 auto" }}>
            <ChatHistoryPanel
              open={showChatHistory}
              placement="sidebar"
              title="履歴（最新10件）"
              loading={historyLoading}
              threads={threads}
              activeThreadId={currentThreadId}
              onClose={() => setShowChatHistory(false)}
              onRefresh={refreshThreadHistory}
              onLoadThread={async (t) => {
                const tid = t.id || t.threadId;
                if (tid) {
                  setCurrentThreadId(tid);
                }
                await aiChatRef.current?.loadThread(t, "agent");
              }}
            />
            <AIChat
              ref={aiChatRef}
              isOpen={showAIChat}
              onClose={() => setShowAIChat(false)}
              onPreviewDiff={(before, after, nextStructuredDiff) => {
                setDocumentBefore(before);
                setDocumentAfter(after);
                setStructuredDiff(nextStructuredDiff ?? null);
                setIsDiffValid(true);
                setDiffModalOpen(before != null && after != null);
              }}
              height="calc(100vh - 196px)"
              maxHeight="none"
              currentDocumentId={currentProject?.outputs?.["field-design"]?.id || ""}
              currentProjectId={currentProject?.id || ""}
              currentDocumentType="field-design"
              originalDocument={isStructuredDocument ? rawContent : markdownContent}
              threadId={currentSession?.threadId}
              resourceId={currentSession?.resourceId}
              onOpenHistory={async () => {
                setShowChatHistory(true);
                await refreshThreadHistory();
              }}
              onThreadUpdate={refreshThreadHistory}
              onThreadChange={(tid) => setCurrentThreadId(tid)}
            />
          </div>
        )}
      </div>

      {!showAIChat && !isEditable && !isKnowledgeSidebarOpen && shouldShowChatIcon && (
        <div className={chatStyles["floatingButtons"]}>
          <KnowledgeIndicator
            documentType="field-design"
            activeCount={knowledgeCount}
            totalCount={knowledgeTotalCount}
            compact
            onOpen={() => setIsKnowledgeSidebarOpen(true)}
          />
          <button
            type="button"
            className={chatStyles["chatButton"]}
            onClick={() => setShowAIChat(true)}
            aria-label="AIチャットを開く"
          >
            <ChatIcon />
          </button>
        </div>
      )}

      <Modal
        label="差分プレビュー"
        isOpen={isDiffModalOpen && !!documentBefore && !!documentAfter}
        onClose={handleDiffClose}
        className="diffPreviewModal"
        usePortal
        subHeader={diffViewerHeader}
        content={
          !!documentBefore && !!documentAfter ? (
            <div className={chatStyles["diffModalContent"]}>
              <div className={chatStyles["diffModalScroll"]}>
                {isStructuredDocument ? (
                  <StructuredDiffErrorBoundary
                    onError={() => {
                      setIsDiffValid(false);
                      setDiffModalOpen(false);
                      toast.error(TOAST_MESSAGES.AI_CHAT.STRUCTURED_DIFF_PARSE_FAILED, {
                        toastId: "structured-diff-parse-failed",
                      });
                    }}
                  >
                    <StructuredDiffViewer
                      originalContent={
                        typeof documentBefore === "string"
                          ? documentBefore
                          : JSON.stringify(documentBefore)
                      }
                      updatedContent={
                        typeof documentAfter === "string"
                          ? documentAfter
                          : JSON.stringify(documentAfter)
                      }
                      documentType="field-design"
                      structuredDiff={structuredDiff}
                      onValidityChange={setIsDiffValid}
                    />
                  </StructuredDiffErrorBoundary>
                ) : (
                  <DiffViewer
                    ref={diffViewerRef}
                    originalContent={
                      typeof documentBefore === "string"
                        ? documentBefore
                        : JSON.stringify(documentBefore, null, 2)
                    }
                    updatedContent={
                      typeof documentAfter === "string"
                        ? documentAfter
                        : JSON.stringify(documentAfter, null, 2)
                    }
                    onApprovedDocumentChange={handleApprovedDocumentChange}
                    onClose={handleDiffClose}
                    renderHeaderExternally
                    onHeaderChange={setDiffViewerHeader}
                  />
                )}
              </div>
            </div>
          ) : null
        }
        footer={
          <div className={chatStyles["diffModalFooter"]}>
            <button
              type="button"
              className={`${chatStyles["diffFooterButton"]} ${chatStyles["diffFooterButtonSecondary"]}`}
              onClick={handleDiffClose}
              disabled={isDiffSaving}
            >
              キャンセル
            </button>
            <button
              type="button"
              className={`${chatStyles["diffFooterButton"]} ${chatStyles["diffFooterButtonPrimary"]}`}
              onClick={handleDiffApply}
              disabled={isDiffSaving || !documentBefore || !documentAfter || !isDiffValid}
            >
              反映する
            </button>
          </div>
        }
      />
    </div>
  );
};

export default FieldDesign;
